import React from 'react';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { Box, CardContent, Skeleton, useTheme, LinearProgress, alpha } from '@mui/material';
import {
  TaskStatusSummaryExternal,
  TaskTypeSummaryExternal,
  TaskStatusType,
  TaskFullSla,
  AssetSummaryExternal,
  UserSummaryExternal,
  TaskPriorityReadExternal,
} from '@askporter/client-grieg-lyric';
import { useToast } from '@askporter/toast';
import { copyToClipboard, formatDate } from '@askporter/utils';
import { Avatar, AvatarSize } from '../../Avatar';
import { BreadcrumbProps } from '../../Breadcrumb';
import { Breadcrumbs } from '../../Breadcrumbs';
import { Chip } from '../../Chip';
import Icon, { IconSize } from '../../Icon';
import { Typography } from '../../Typography';
import { TaskCardPersonAvatar } from '../TaskCardPersonAvatar';
import { TaskCardSlaDisplay } from '../TaskCardSlaDisplay';

export interface TaskCardContentsProps {
  taskType?: TaskTypeSummaryExternal;
  title?: string;
  taskRef?: string;
  status?: TaskStatusSummaryExternal;
  sla?: TaskFullSla;
  closedAt?: string;
  principalAsset?: AssetSummaryExternal;
  assignedUser?: UserSummaryExternal;
  priority?: TaskPriorityReadExternal;
  initiator?: UserSummaryExternal;
  openedAt?: string;
  permissions?: Record<string, boolean>;
  value?: string;
  onClick?: () => void;
  isLoading?: boolean;
  t: (key: string) => string;
  captureException?: (error: Error) => void;
  slaFilter?: 'RESPONSE' | 'RESOLUTION';
}

export interface TooltipState {
  visible: boolean;
  message: string;
}

export const TaskCardContents: React.FC<React.PropsWithChildren<TaskCardContentsProps>> = ({
  taskType,
  title,
  taskRef,
  status,
  sla,
  principalAsset,
  assignedUser,
  priority,
  initiator,
  openedAt,
  permissions: { isManager = false, isSuperServiceUser = false } = {},
  value,
  onClick,
  isLoading,
  t,
  slaFilter,
  // eslint-disable-next-line no-console
  captureException = console.error,
}: TaskCardContentsProps): JSX.Element => {
  const { showSuccess, showError } = useToast();
  const {
    palette: {
      common: { white: commonWhite },
      grey,
    },
  } = useTheme();

  const copyFunction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const isSuccess = copyToClipboard(`${window.location.origin}/app/tasks/${value}`, (...args: unknown[]) => {
      if (args[0] instanceof Error) {
        captureException(args[0]);
      }
    });
    const message = isSuccess
      ? t('ns.task_list:task_link:clipboard_success')
      : t('ns.task_list:task_link:clipboard_error');
    if (isSuccess) {
      showSuccess(message);
    } else {
      showError(message);
    }
    return message;
  };

  const taskTypeIconColour = status?.statusType === TaskStatusType.Closed ? grey[900] : taskType?.icon?.iconColourMask;
  const priorityChipColour = status?.statusType === TaskStatusType.Closed ? grey[900] : priority?.icon?.iconColourMask;

  const openedAtDate = openedAt ? formatDate(openedAt, 'dd MMM yy HH:mm') : undefined;

  const breadcrumbs = React.useMemo(() => {
    const items: BreadcrumbProps[] = [];
    if (taskType?.taskTypeGroup?.displayName) {
      items.push({ label: taskType.taskTypeGroup.displayName });
    }
    if (taskType?.displayName) {
      items.push({ label: taskType.displayName });
    }
    if (taskRef) {
      items.push({ label: taskRef });
    }
    return items;
  }, [taskType?.taskTypeGroup?.displayName, taskType?.displayName, taskRef]);

  return (
    <Box
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        height: '100%',
      }}
      onClick={onClick}
    >
      <Box data-testid="highlight">
        <Box
          sx={{
            flex: 1,
            justifyContent: 'space-between',
            mb: 3,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          <Box display="flex" alignItems={'center'}>
            <div data-testid="base-card-breadcrumbs">
              <Breadcrumbs items={breadcrumbs} onCopy={isManager && !isLoading ? copyFunction : undefined} />
            </div>
          </Box>
        </Box>
        <Box display="flex">
          <Box mr={4}>
            <Avatar
              dataTestId={`${value}-task-card-header-avatar`}
              altText={title}
              isLoading={isLoading}
              avatar={{
                mode: 'icon',
                variant: 'rounded',
                imagePath: taskType?.icon?.iconPath,
                colorMask: taskTypeIconColour,
                bgcolor: taskTypeIconColour ? commonWhite : undefined, // where a colour mask is applied we want a white bg
                fallback: <></>,
              }}
            />
          </Box>
          <Box sx={{ flex: 1, textOverflow: 'ellipsis', overflow: 'hidden' }}>
            <Typography noWrap variant="h3" data-testid={`${value}-task-card-header-title`}>
              {isLoading ? <Skeleton variant="text" animation="wave" width={AvatarSize.XL * 2} /> : title}
            </Typography>
            {isLoading ? (
              <Skeleton variant="text" animation="wave" width={AvatarSize.XL * 1.5} />
            ) : (
              <Chip
                data-testid={`${value}-task-card-header-subtitle`}
                label={principalAsset ? principalAsset.title : t('task_details:primary_asset_not_selected')}
                startAdornment={{
                  mode: 'icon',
                  icon: principalAsset?.icon?.iconPath ? (
                    <Icon
                      size={IconSize.SM}
                      fullPath={principalAsset.icon.iconPath}
                      fillColor={'inherit'}
                      isStrokeColor
                    />
                  ) : (
                    <ViewInArIcon fontSize="inherit" />
                  ),
                }}
                color="default"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? null : (
            <>
              <Box mb={4}>
                <Box display="flex" flexWrap="nowrap" alignItems="flex-end" justifyContent={'space-between'} mb={2}>
                  <Typography
                    variant="body2"
                    data-testid="task-card-status"
                    color="textSecondary"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {status?.displayName ? status?.displayName : ''}
                  </Typography>
                  <Typography
                    component="label"
                    variant="body2"
                    color="textSecondary"
                    id="task-progress-percentage-value"
                    data-testid="progress-percentage-value"
                  >
                    {status?.progressIndicator !== undefined && status.progressIndicator >= 0
                      ? `${status.progressIndicator}%`
                      : ''}
                  </Typography>
                </Box>
                {status?.progressIndicator ? (
                  <LinearProgress
                    data-testid="progress-bar"
                    aria-labelledby="task-progress-percentage-value"
                    variant="determinate"
                    value={status?.progressIndicator}
                    sx={{ backgroundColor: 'background.default', borderRadius: 9999, height: '8px' }}
                  />
                ) : null}
              </Box>
              <Box
                display="flex"
                flex={1}
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="space-between"
                minHeight="24px"
              >
                <Box display="flex" flex={1} flexWrap="nowrap" alignItems="center">
                  <TaskCardPersonAvatar t={t} userType="assignee" user={assignedUser} isManager />
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                    {(isManager || isSuperServiceUser) && priority ? (
                      <Chip
                        label={priority?.displayName}
                        variant="filled"
                        size="small"
                        startAdornment={{
                          mode: 'icon',
                          icon: (
                            <Icon
                              fullPath={priority?.icon?.iconPath}
                              size={IconSize.SM}
                              fillColor={priorityChipColour ? priorityChipColour : 'inherit'}
                            />
                          ),
                        }}
                        sx={{
                          backgroundColor: priorityChipColour ? alpha(priorityChipColour, 0.04) : undefined,
                          border: priorityChipColour ? `1px solid ${alpha(priorityChipColour, 0.32)}` : undefined,
                          ...(priorityChipColour && {
                            '& .MuiChip-label': { color: priorityChipColour },
                          }),
                        }}
                      />
                    ) : null}
                    <TaskCardSlaDisplay sla={sla} t={t} slaFilter={slaFilter} />
                    {status?.statusType === TaskStatusType.Future && (
                      <Chip
                        data-testid="scheduled-chip"
                        color="success"
                        label={t('ns.task_list:task_card:future')}
                        size="small"
                        startAdornment={{
                          mode: 'icon',
                          icon: (
                            <Icon
                              size={IconSize.SM}
                              folder={'status/02-task-details/01-task-status'}
                              fillColor={'inherit'}
                              isStrokeColor
                            />
                          ),
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box display="flex" flexDirection="row" alignSelf="center" alignItems="center">
                  <TaskCardPersonAvatar t={t} userType="initiator" user={initiator} isManager />
                  {openedAtDate && (
                    <Typography data-testid="opened-at-date" variant="body2" color="textSecondary">{`${t?.(
                      'ns.common:created',
                    )} ${openedAtDate}`}</Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Box>
    </Box>
  );
};
